<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay">
      <div class="modal-content" style="max-width: 600px">
        <div class="modal-header">
          <h3>{{ s__('Boards|Choose Transition') }}</h3>
          <button class="modal-close" @click="handleCancel" aria-label="Close">×</button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="loading-message">
            {{ s__('Boards|Loading transitions...') }}
          </div>
          <div v-else-if="transitions.length === 0" class="no-transitions-message">
            <p class="warning-text">
              {{ s__('Boards|No transitions available') }}
            </p>
            <p class="warning-detail">
              {{
                s__(
                  'Boards|You do not have permission to change the status of this task, or there are no valid transitions from the current status.'
                )
              }}
            </p>
          </div>
          <div v-else>
            <p v-if="transitions.length > 1" style="margin-bottom: 16px">
              {{ s__('Boards|Multiple transitions are available. Please select one:') }}
            </p>
            <p v-else style="margin-bottom: 16px">
              {{ s__('Boards|One transition is available:') }}
            </p>
            <div class="transitions-list">
              <button
                v-for="transition in transitions"
                :key="transition.id"
                type="button"
                class="btn-secondary transition-button-modal"
                :class="{
                  'is-selected': selectedTransition?.id === transition.id,
                  'transition-disabled-checklist': isTransitionDisabled(transition),
                }"
                :disabled="isTransitionDisabled(transition)"
                :title="getTransitionTooltip(transition)"
                @click="selectTransition(transition)"
              >
                <div class="transition-main-row">
                  <span class="transition-name">{{ transition.name || s__('Boards|Move') }}</span>
                  <span class="transition-status-main">
                    <span class="transition-arrow">
                      <PlayIcon />
                    </span>
                    <span
                      v-if="transition.targetStatus?.color"
                      class="status-dot"
                      :style="{ backgroundColor: transition.targetStatus.color }"
                    ></span>
                    {{ transition.targetStatus.name }}
                    <span
                      v-if="isTransitionDisabled(transition)"
                      class="checklist-lock-icon"
                      :title="getTransitionTooltip(transition)"
                    >
                      🔒
                    </span>
                  </span>
                </div>
                <div
                  v-if="getTransitionDestinationHint(transition)"
                  class="transition-destination-hint-row"
                >
                  <span class="transition-destination-hint">
                    {{ getTransitionDestinationHint(transition) }}
                  </span>
                </div>
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn-secondary" @click="handleCancel">
            {{ s__('Boards|Cancel') }}
          </button>
          <button
            type="button"
            class="btn-primary"
            @click="handleConfirm"
            :disabled="!selectedTransition || executing || transitions.length === 0"
          >
            <span v-if="executing">{{ s__('Boards|Executing...') }}</span>
            <span v-else>{{ s__('Boards|Confirm') }}</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { s__ } from '@/locale'
import PlayIcon from '../common/icons/PlayIcon.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

const props = defineProps({
  visible: { type: Boolean, default: false },
  transitions: { type: Array, default: () => [] },
  task: { type: Object, default: null },
  boardColumns: { type: Array, default: () => [] },
  checklistsEnabled: { type: Boolean, default: false },
  executing: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['confirm', 'cancel'])

// Lock body scroll when modal is open
useBodyScrollLock(computed(() => props.visible))

const selectedTransition = ref(null)

watch(
  () => props.transitions,
  newTransitions => {
    if (newTransitions && newTransitions.length > 0 && !selectedTransition.value) {
      selectedTransition.value = newTransitions[0]
    }
  },
  { immediate: true }
)

watch(
  () => props.visible,
  newVisible => {
    if (!newVisible) {
      selectedTransition.value = null
    } else if (props.transitions && props.transitions.length > 0) {
      selectedTransition.value = props.transitions[0]
    }
  }
)

function selectTransition(transition) {
  selectedTransition.value = transition
}

function handleConfirm() {
  if (selectedTransition.value) {
    emit('confirm', selectedTransition.value)
  }
}

function handleCancel() {
  emit('cancel')
}

// Check if a specific transition should be disabled due to checklist requirements
function isTransitionDisabled(transition) {
  // Check if target status requires completed checklists
  if (!transition.targetStatus?.requiresCompletedChecklists) return false

  // Check if feature flag is enabled
  if (!props.checklistsEnabled) return false

  // Check if task has a checklist
  if (!props.task?.checklist) return false

  // Check if checklist is incomplete
  if (props.task?.checklistCompleted) return false

  return true
}

// Get tooltip text for disabled transition
function getTransitionTooltip(transition) {
  if (!isTransitionDisabled(transition)) return ''
  return s__('Boards|Checklist must be completed before transitioning to this status')
}

function getTransitionDestinationHint(transition) {
  const targetStatusId = transition?.targetStatus?.id
  if (!targetStatusId || !props.boardColumns?.length) return ''

  const destinationColumns = props.boardColumns.filter(column =>
    (column.workflowStatuses || []).some(status => String(status.id) === String(targetStatusId))
  )

  if (destinationColumns.length === 0) {
    return s__('Boards|Not mapped to a board column')
  }

  if (destinationColumns.length === 1) {
    return `${s__('Boards|Column:')} ${destinationColumns[0].title}`
  }

  return `${s__('Boards|Columns:')} ${destinationColumns.map(column => column.title).join(', ')}`
}
</script>

<style scoped>
/* Component-specific: max-width override */
.modal-content {
  max-width: 600px;
}

.transitions-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Modal transition buttons - styled as secondary buttons with custom layout */
.transition-button-modal {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 6px;
  width: 100%;
  padding: 12px 24px;
}

.transition-button-modal .transition-main-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.transition-button-modal .transition-name {
  background: transparent;
  color: inherit;
  padding: 0;
  margin-right: 12px;
  font-weight: 500;
}

.transition-button-modal .transition-name::after {
  display: none;
}

.transition-button-modal .transition-arrow {
  color: #6b7280;
  margin-right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.transition-button-modal .transition-arrow svg {
  width: 16px;
  height: 16px;
}

.transition-button-modal .transition-status-main {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.transition-button-modal .transition-destination-hint-row {
  width: 100%;
  text-align: right;
}

.transition-button-modal .transition-destination-hint {
  font-size: 12px;
  color: #666;
  line-height: 1.3;
}

.transition-button-modal.is-selected {
  border-color: #2aabee !important;
  background: #e7f3ff4b !important;
}

.transition-button-modal.transition-disabled-checklist {
  border-color: #dc3545 !important;
  background: #fff5f5 !important;
  color: #dc3545 !important;
}

.transition-button-modal.transition-disabled-checklist:hover:not(:disabled) {
  background: #ffe0e0 !important;
  border-color: #dc3545 !important;
}

.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.checklist-lock-icon {
  margin-left: 6px;
  font-size: 12px;
  opacity: 0.8;
}

/* Button styles inherit from forms.css */

.loading-message {
  text-align: center;
  padding: 20px;
  color: #666;
}

.no-transitions-message {
  text-align: center;
  padding: 20px;
}

.warning-text {
  font-weight: 600;
  color: #dc3545;
  margin-bottom: 8px;
  font-size: 16px;
}

.warning-detail {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}
</style>
